import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { TPlayerGameCard, Try } from '../types/commonTypes'
import { useUser } from './userContext'
import { useGameNotifications } from '../hooks/useGameNotifications'
import { parsePlayerAccountGamesResponse } from '../api/typeConverters'
import {
  changePlayerAccountPasswordRequest,
  getPlayerAccountPasswordTokenRequest,
  getUserInfo,
  playerAccountJoinNewGameRequest,
  playerAccountJoinNewGameWithNameRequest,
  playerAccountUpdateRequest,
} from '../api/userApiService'
import { PlayerAccountJoinGameResponse } from '../api/userTypes'

export enum GameSortBy {
  NAME_ASC = 'name asc',
  NAME_DESC = 'name desc',
  UPDATED_AT_ASC = 'updated_at asc',
  UPDATED_AT_DESC = 'updated_at desc',
}

export type GameDataProviderType = {
  pageData: TPlayerGameCard[] | undefined
  loading: boolean
  refreshGames: () => void
  joinNewGame: (pinCode: string) => Promise<Try<PlayerAccountJoinGameResponse>> | null
  joinNewGameWithName: (
    teamName: string,
    newGameId: string,
    newUserId: string,
  ) => Promise<Try<PlayerAccountJoinGameResponse>> | null
  updatePlayerAccount: (
    playerAccount?: { email: string; playerName: string; password: string; gamerAccountConfirmPassword: string },
    language?: string,
  ) => Promise<Try<PlayerAccountJoinGameResponse>> | null
  getPlayerAccountPasswordToken: (email: string) => Promise<boolean>
  changePlayerAccountPassword: (token: string, newPassword: string) => Promise<boolean>
  sortBy?: GameSortBy
  setSortBy?: (sortBy: GameSortBy) => void
  initialFetchDone?: boolean
}

const PlayerAccountGamesDataContext = createContext<GameDataProviderType | null>(null)

export const PlayerAccountGamesDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState<GameSortBy>(GameSortBy.UPDATED_AT_DESC)
  const [pageData, setPageData] = useState<TPlayerGameCard[]>()
  const { user, refreshUser } = useUser()
  const [initialFetchDone, setInitialFetchDone] = useState<boolean>(false)

  const { notifyFetchingGamesFailed } = useGameNotifications()

  useEffect(() => {
    if (user?.id == null) {
      return
    }
    const abortController = new AbortController()
    setLoading(true)
    getUserInfo({})
      .then((userResponse) => {
        if (userResponse.success) {
          setPageData(parsePlayerAccountGamesResponse(userResponse.value))
        } else {
          notifyFetchingGamesFailed()
          console.error(userResponse.error)
        }
      })
      .finally(() => {
        setLoading(false)
        setInitialFetchDone(true)
      })
    return () => abortController.abort()
  }, [sortBy, user?.id, notifyFetchingGamesFailed, user])

  const refreshGames = useCallback(() => {
    if (user?.id == null) {
      return
    }
    getUserInfo({}).then((userResponse) => {
      if (userResponse.success) {
        setPageData(parsePlayerAccountGamesResponse(userResponse.value))
      } else {
        notifyFetchingGamesFailed()
        console.error(userResponse.error)
      }
    })
  }, [notifyFetchingGamesFailed, user?.id])

  const joinNewGame = useCallback(
    async (pinCode: string) => {
      const response = await playerAccountJoinNewGameRequest({ pinCode })
      if (response.success) {
        refreshGames()
      } else {
        console.error(response.error)
      }
      return response
    },
    [refreshGames],
  )

  const joinNewGameWithName = useCallback(
    async (teamName: string, newGameId: string, newUserId: string) => {
      const response = await playerAccountJoinNewGameWithNameRequest({ teamName, newGameId, newUserId })
      if (response.success) {
        refreshGames()
      } else {
        console.error(response.error)
      }
      return response
    },
    [refreshGames],
  )

  const updatePlayerAccount = useCallback(
    async (
      playerAccount?: { email: string; playerName: string; password: string; gamerAccountConfirmPassword: string },
      language?: string,
    ) => {
      const response = await playerAccountUpdateRequest({ playerAccount, language })
      if (response.success) {
        refreshUser()
        refreshGames()
      } else {
        console.error(response.error)
      }
      return response
    },
    [refreshGames, refreshUser],
  )

  const getPlayerAccountPasswordToken = useCallback(async (email: string) => {
    if (email == null) {
      console.warn('Cannot request password reset, user email is not set')
      return false
    } else {
      const response = await getPlayerAccountPasswordTokenRequest({ email })
      return response.success
    }
  }, [])

  const changePlayerAccountPassword = useCallback(async (token: string, newPassword: string) => {
    const response = await changePlayerAccountPasswordRequest({ token, newPassword })
    return response.success
  }, [])

  return (
    <PlayerAccountGamesDataContext.Provider
      value={{
        pageData,
        loading,
        initialFetchDone,
        sortBy,
        joinNewGame,
        joinNewGameWithName,
        updatePlayerAccount,
        getPlayerAccountPasswordToken,
        changePlayerAccountPassword,
        refreshGames,
        setSortBy,
      }}
    >
      {children}
    </PlayerAccountGamesDataContext.Provider>
  )
}

export const useGameData = () => {
  const context = useContext(PlayerAccountGamesDataContext)
  if (!context) {
    throw new Error('Expected to be wrapped in GameContext!')
  }
  return context
}
